/**
 * @fileoverview System-owned data report definitions grid for system-web.
 * @module system-web/pages/reports/SystemDataReportsPage
 *
 * Responsibilities: - List system-scope data report definitions from the
 *                     system Dexie DB (liveQuery-reactive).
 *                   - Surface library curation, publish/archive lifecycle,
 *                     visibility, and scheduled-delivery state.
 *                   - Row actions: edit, run-with-params, schedule, duplicate,
 *                     publish, archive, library toggle, delete.
 * Dependencies:     - @firesquirrel/ui-admin: GridPageShell, StatusBadge,
 *                     ScheduleBadge, action builders, PRESET_LABELS.
 *                   - ../../services/systemReportDefinitionService (Dexie reads
 *                     + mutations).
 *                   - ../../services/systemReportScheduleService (schedule list).
 * Architecture:     - Local-first: reads via liveQuery on systemDb tables;
 *                     mutations write Dexie and sync in the background.
 *                   - Route: /system-data-reports (system-web).
 * Notes:            - Library column only meaningful for scope.level='system'.
 */

import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, Plus, Globe, Lock, Send } from 'lucide-react';
import { GridPageShell } from '@ui-admin/components/GridPageShell';
import type { DataGridColumn } from '@ui-admin/components/DataGrid/types';
import {
  StatusBadge,
  ScheduleBadge,
  Badge,
  PRESET_LABELS,
  makeEditAction,
  makeDuplicateAction,
  makeArchiveAction,
  makeDeleteAction,
  makeScheduleAction,
  makeRunWithParametersAction,
  type DataGridRowAction,
  type SchedulePreset,
} from '@firesquirrel/ui-admin';
import type { ReportDefinitionLocal, ReportScheduleLocal } from '@firesquirrel/local-db';
import {
  archiveReportDefinition,
  deleteReportDefinition,
  duplicateReportDefinition,
  publishReportDefinition,
  toggleLibrary,
  watchReportDefinitions,
} from '../../services/systemReportDefinitionService';
import { watchReportSchedules } from '../../services/systemReportScheduleService';
import { ConfirmActionModal } from '../../components/ui';

/**
 * @component SystemDataReportsPage
 * Purpose:     Manage system-scope data report definitions (authoring +
 *              library curation) in system-web.
 * Props:       (none — route component)
 * Behavior:    liveQuery-backed grid; slide-in detail on row click opens the
 *              builder directly (system pages navigate rather than panel).
 * Data Source: local (systemDb via systemReport*Service)
 * Constraints: no direct API calls; lifecycle mutations through the service.
 * Performance: renders from cached Dexie state.
 */
export default function SystemDataReportsPage() {
  const navigate = useNavigate();
  const [reports, setReports] = useState<ReportDefinitionLocal[]>([]);
  const [schedules, setSchedules] = useState<ReportScheduleLocal[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<ReportDefinitionLocal | null>(null);

  // Local-first subscription — watchReportDefinitions reads the raw
  // `report_definitions` table inside liveQuery's observation zone.
  useEffect(() => {
    const subscription = watchReportDefinitions(
      { type: 'report', scopeLevel: 'system' },
      {
      next: (data) => {
        setReports(data);
        setLoading(false);
      },
      error: (err: unknown) => {
        console.error('[SystemDataReportsPage] Failed to load reports:', err);
        setLoading(false);
      },
      },
    );
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const subscription = watchReportSchedules(undefined, {
      next: setSchedules,
      error: (err) => console.error('[SystemDataReportsPage] Failed to load schedules:', err),
    });
    return () => subscription.unsubscribe();
  }, []);

  const activeSchedulesByReport = useMemo(() => {
    const map = new Map<string, ReportScheduleLocal[]>();
    for (const s of schedules) {
      if (!s.isActive) continue;
      const list = map.get(s.reportDefinitionId) ?? [];
      list.push(s);
      map.set(s.reportDefinitionId, list);
    }
    return map;
  }, [schedules]);

  const columns = useMemo<DataGridColumn<ReportDefinitionLocal>[]>(() => [
    {
      id: 'name',
      header: 'Report Name',
      accessorKey: 'name',
      cell: (info) => {
        const row = info.row.original;
        return (
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-900">{row.name}</span>
              {(row.parameterIds?.length ?? 0) > 0 && (
                <Badge
                  label={`${row.parameterIds!.length} Param${row.parameterIds!.length !== 1 ? 's' : ''}`}
                  variant="neutral"
                />
              )}
            </div>
            {row.description && (
              <div className="text-sm text-gray-500">{row.description}</div>
            )}
          </div>
        );
      },
    },
    {
      id: 'status',
      header: 'Status',
      accessorKey: 'status',
      cell: (info) => <StatusBadge status={info.getValue() as string} />,
    },
    {
      id: 'visibility',
      header: 'Visibility',
      cell: (info) => {
        const row = info.row.original;
        const roleCount = row.visibleToRoleIds?.length ?? 0;
        if (roleCount === 0) {
          return <span className="text-xs text-gray-400">All roles</span>;
        }
        return <Badge label={`${roleCount} role${roleCount !== 1 ? 's' : ''}`} variant="info" />;
      },
    },
    {
      id: 'library',
      header: 'Library',
      cell: (info) => {
        const row = info.row.original;
        if (row.scope?.level !== 'system') return <span className="text-gray-300">—</span>;
        return row.isInLibrary
          ? <span className="inline-flex items-center gap-1 text-xs text-green-700"><Globe className="h-3.5 w-3.5" /> In Library</span>
          : <span className="inline-flex items-center gap-1 text-xs text-gray-400"><Lock className="h-3.5 w-3.5" /> Not in Library</span>;
      },
    },
    {
      id: 'schedule',
      header: 'Delivery',
      cell: (info) => {
        const rowSchedules = activeSchedulesByReport.get(info.row.original._id) ?? [];
        const preset = rowSchedules[0]?.schedule?.preset as SchedulePreset | undefined;
        return (
          <ScheduleBadge
            scheduleCount={rowSchedules.length}
            cadenceLabel={preset ? PRESET_LABELS[preset] : undefined}
            scheduleSummaries={rowSchedules.map((s) => `${s.name} · next ${s.nextRunAt ? String(s.nextRunAt).slice(0, 10) : '—'}`)}
          />
        );
      },
    },
    {
      id: 'updatedAt',
      header: 'Last Updated',
      accessorKey: 'updatedAt',
      cell: (info) => new Date(info.row.original.updatedAt).toLocaleDateString(),
    },
  ], [activeSchedulesByReport]);

  const rowActions = useMemo<DataGridRowAction<ReportDefinitionLocal>[]>(() => [
    makeRunWithParametersAction<ReportDefinitionLocal>(
      (row) => navigate(`/data-report-templates/${row._id}?expand=parameters`),
      { hidden: (row) => !(row.parameterIds?.length) },
    ),
    makeEditAction<ReportDefinitionLocal>((row) => navigate(`/data-report-templates/${row._id}`)),
    makeScheduleAction<ReportDefinitionLocal>(
      (row) => navigate(`/data-report-templates/${row._id}?expand=schedule`),
    ),
    makeDuplicateAction<ReportDefinitionLocal>(async (row) => {
      await duplicateReportDefinition(row._id);
    }),
    {
      id: 'publish',
      label: 'Publish',
      icon: Send,
      onClick: async (row) => { await publishReportDefinition(row._id); },
      hidden: (row) => row.status === 'published' || row.status === 'archived',
    },
    makeArchiveAction<ReportDefinitionLocal>(async (row) => {
      await archiveReportDefinition(row._id);
    }, { hidden: (row) => row.status === 'archived' }),
    {
      id: 'addToLibrary',
      label: 'Add to Library',
      icon: Globe,
      onClick: async (row) => { await toggleLibrary(row._id, true); },
      hidden: (row) => row.scope?.level !== 'system' || row.status !== 'published' || !!row.isInLibrary,
    },
    {
      id: 'removeFromLibrary',
      label: 'Remove from Library',
      icon: Lock,
      onClick: async (row) => { await toggleLibrary(row._id, false); },
      hidden: (row) => row.scope?.level !== 'system' || !row.isInLibrary,
    },
    makeDeleteAction<ReportDefinitionLocal>((row) => setDeleteTarget(row)),
  ], [navigate]);

  if (loading) return null;

  return (
    <>
    <GridPageShell
      gridId="system-data-reports"
      gridType="data-reports"
      title="System Data Reports"
      subtitle={`${reports.length} ${reports.length === 1 ? 'system report' : 'system reports'} ready for authoring and library curation`}
      columns={columns}
      data={reports}
      getRowId={(row) => row._id}
      searchPlaceholder="Search system data reports..."
      toolbarActions={
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/data-report-templates/new')}
            className="inline-flex items-center gap-2 px-4 py-2.5 text-white bg-blue-600 hover:bg-blue-700 rounded-lg text-sm font-medium transition-colors"
          >
            <Plus className="h-4 w-4" />
            New System Report
          </button>
        </div>
      }
      emptyIcon={BarChart3}
      emptyTitle="No system data reports"
      emptySubtitle="Create your first system-owned report, then publish it to add it to the library."
      emptyCTA={{ label: 'New System Report', onClick: () => navigate('/data-report-templates/new') }}
      onRowClick={(row) => navigate(`/data-report-templates/${row._id}`)}
      rowActions={rowActions}
    />
    {deleteTarget && (
      <ConfirmActionModal
        title={`Delete ${deleteTarget.name}?`}
        description={<>Are you sure you want to delete <strong>{deleteTarget.name}</strong>? This action cannot be undone.</>}
        confirmButtonLabel="Delete Report"
        variant="danger"
        onConfirm={async () => {
          await deleteReportDefinition(deleteTarget._id);
          setDeleteTarget(null);
        }}
        onClose={() => setDeleteTarget(null)}
      />
    )}
    </>
  );
}
